import React, { CSSProperties, ReactElement, useState } from 'react';

import format from 'date-fns/format';
import { capitalize, last, range, sortBy } from 'lodash';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { createTransformer } from 'mobx-utils';

import { Job, issueReasons } from '@vromo/models';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import ObservableAutoSizer from '@vromo/ui/components/ObservableAutoSizer';
import AuditHistoryModal from './components/AuditHistoryModal';
import ChatHistoryModal from './components/ChatHistoryModal';
import GridHeaderCell from './components/GridHeaderCell';
import ObservableMultiGrid from './components/ObservableMultiGrid';
import PhotoHistoryModal from './components/PhotoHistoryModal';
import { CommentLink, GridCellDisplay } from './components/styledComponents';
import getReadableFromDateRange from './helpers/getReadableFromDateRange';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

type Column = {
    label: string;
    width?: number;
    getValue: (job: Job) => string | number;
    render?: (job: Job) => ReactElement;
    sortable?: string;
    align?: 'left' | 'right' | 'center';
};

interface Props {
    jobs: Job[];
}

export default observer<Props>(function JobGrid({ jobs }) {
    const [selectedJob, setSelectedJob] = useState<Job>();
    const [showPhotoHistory, setShowPhotoHistory] = useState(false);
    const [showChatHistory, setShowChatHistory] = useState(false);
    const [showAuditHistory, setShowAuditHistory] = useState(false);

    const store = useLocalObservable(() => ({
        sortColumn: 0,
        sortDirection: 1,
        get rowCount(): number {
            return store.sortedJobs.length + 1;
        },

        get sortedJobs(): Job[] {
            const sortColumn = this.columns[this.sortColumn];
            if (!sortColumn) throw new Error(`Invalid sort column index ${this.sortColumn}`);
            const sortedJobs = sortBy(jobs, (it) => sortColumn.getValue(it));
            if (this.sortDirection === -1) sortedJobs.reverse();

            return sortedJobs;
        },

        get maxTasks() {
            return Math.max(...(jobs.map((it: Job) => it.tasks.items.length) || []), 0);
        },

        get maxIssues() {
            return Math.max(...(jobs.map((it: Job) => it.issues!.items.length) || []), 0);
        },

        get maxPhotos() {
            return Math.max(
                ...(jobs.map(
                    (it: Job) => it.uploads!.items.filter((u) => u.category === 'photo').length
                ) || []),
                0
            );
        },

        get attrSet() {
            const result = new Set<string>();

            jobs.forEach((job) => {
                job.attr &&
                    job.attr.items.forEach((attr) => {
                        if (attr.key && attr.key !== 'deliverBy') {
                            result.add(attr.key);
                        }
                    });
            });

            return result;
        },

        get columns() {
            return [
                {
                    label: 'Job Name',
                    width: 300,
                    getValue: createTransformer((job: Job) => {
                        return /* orderNo: */ job.name;
                    })
                },
                {
                    label: 'Status',
                    getValue: createTransformer((job: Job) => {
                        return /* status: */ capitalize(job.status);
                    })
                },
                {
                    label: 'Driver',
                    getValue: createTransformer((job: Job) => {
                        const lastVehicle = last(job.dispatches?.items)?.vehicle;
                        return lastVehicle?.driver?.contact?.name ?? null;
                    })
                },
                {
                    label: 'Contact Name',
                    getValue: createTransformer((job: Job) => {
                        return /* contactName: */ job.contact?.name;
                    })
                },
                {
                    label: 'Contact Email',
                    width: 200,
                    getValue: createTransformer((job: Job) => {
                        return /* contactEmail: */ job.contact?.email;
                    })
                },
                {
                    label: 'Contact Phone',
                    getValue: createTransformer((job: Job) => {
                        return String(job.contact?.phone || '');
                    })
                },
                // {
                //     label: 'SMS Opt-in',
                //     getValue: createTransformer((job: Job) => {
                //         return String(job?.optedInForCampaignSms ? 'Yes' : 'No');
                //     })
                // },
                // {
                //     label: 'Deliver By',
                //     getValue: createTransformer((job: Job) => {
                //         return job.timeframe?.by
                //             ? format(job.timeframe.by, DATE_TIME_FORMAT)
                //             : 'ASAP';
                //     })
                // },
                {
                    label: 'Created',
                    width: 150,
                    getValue: createTransformer((job: Job) => {
                        return format(job.created!, DATE_TIME_FORMAT);
                    })
                },
                {
                    label: 'Started',
                    width: 150,
                    getValue: createTransformer((job: Job) => {
                        const dispatch = job.dispatches?.items
                            ? job.dispatches.items.find((it) => it.type === 'underway')
                            : null;
                        return dispatch ? format(dispatch.created!, DATE_TIME_FORMAT) : '';
                    })
                },

                ...range(0, this.maxTasks).reduce<Column[]>((taskCols, ix) => {
                    taskCols.push({
                        label: `Task ${ix + 1} Deadline`,
                        width: 150,
                        getValue: createTransformer((job: Job) =>
                            job.tasks.items.length > ix && job.tasks.items[ix]?.timeframe?.by
                                ? format(job.tasks.items[ix]!.timeframe!.by!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });
                    taskCols.push({
                        label: `Task ${ix + 1} Completed`,
                        width: 150,
                        getValue: createTransformer((job: Job) =>
                            job.tasks.items.length > ix && job.tasks.items[ix]?.completed?.time
                                ? format(job.tasks.items[ix]!.completed!.time!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });
                    taskCols.push({
                        label: `Task ${ix + 1} Lateness`,
                        width: 150,
                        align: 'right',
                        getValue: createTransformer(
                            (job: Job) =>
                                (!!job.tasks.items[ix]?.completed?.time &&
                                    !!job.tasks.items[ix]?.timeframe?.by &&
                                    job.tasks.items[ix]!.completed!.time! -
                                        job.tasks.items[ix]!.timeframe!.by!) ||
                                0
                        ),
                        render: createTransformer((job: Job) => (
                            <>
                                {getReadableFromDateRange(
                                    job.tasks.items[ix]?.timeframe?.by,
                                    job.tasks.items[ix]?.completed?.time
                                )}
                            </>
                        ))
                    });

                    return taskCols;
                }, []),

                ...range(0, this.maxIssues).reduce<Column[]>((issueCols, ix) => {
                    issueCols.push({
                        label: `Issue ${ix + 1}`,
                        getValue: createTransformer((job: Job) =>
                            job.issues!.items.length > ix && job.issues!.items[ix]!.type
                                ? String(issueReasons[job.issues!.items[ix]!.type!])
                                : ''
                        )
                    });
                    issueCols.push({
                        label: `Issue ${ix + 1} Created`,
                        getValue: createTransformer((job: Job) =>
                            job.issues!.items.length > ix && job.issues!.items[ix]!.created
                                ? format(job.issues!.items[ix]!.created!, DATE_TIME_FORMAT)
                                : ''
                        )
                    });

                    return issueCols;
                }, []),

                {
                    label: 'Completed',
                    width: 150,
                    getValue: createTransformer((job: Job) => {
                        const completedTime = job.finished;
                        return completedTime ? format(completedTime, DATE_TIME_FORMAT) : '';
                    })
                },

                {
                    label: 'Rating',
                    getValue: createTransformer((job: Job) => {
                        const ratingItems = (job.ratings || {}).items || [];

                        return /* rating: */ ratingItems.length > 0
                            ? (ratingItems[0]!.ratio * ratingScale).toFixed(1)
                            : null;
                    })
                },

                {
                    label: 'Photos',
                    getValue: (job: Job) =>
                        job.uploads!.items.filter((it) => it.category == 'photo').length,
                    render: (job: Job) => (
                        <CommentLink
                            onClick={() => {
                                setSelectedJob(job);
                                setShowPhotoHistory(true);
                            }}>
                            {`${job.uploads!.items.filter((it) => it.category == 'photo').length} `}
                            <FontAwesome icon="images" weight="regular" />
                        </CommentLink>
                    )
                },

                {
                    label: 'Signatures',
                    width: 200,
                    getValue: () => 'signatures',
                    render: (job: Job) => {
                        return (
                            <div>
                                {(job.uploads || { items: [] }).items
                                    .filter((it) => it.category === 'signature')
                                    .map((it, ix) => (
                                        <span key={it.id}>
                                            <a href={it.url!} target="_blank">
                                                {ix + 1} <i className="fas fa-external-link-alt" />
                                            </a>{' '}
                                        </span>
                                    ))}
                            </div>
                        );
                    }
                },

                ...range(0, this.maxTasks).map((ix) => ({
                    label: `Address ${ix + 1}`,
                    width: 400,
                    getValue: createTransformer((job: Job) =>
                        job.tasks.items.length > ix ? job.tasks.items[ix]!.zone!.address : ''
                    )
                })),

                ...range(0, this.attrSet.size).map((ix) => {
                    const attr = [...this.attrSet][ix];

                    return {
                        label: attr || '',
                        width: 200,
                        getValue: createTransformer((job: Job) => {
                            const jobAttr =
                                job.attr && job.attr.items.find((it) => it.key === attr);
                            return jobAttr ? jobAttr.value : '';
                        })
                    };
                }),

                {
                    label: 'Chat History',
                    getValue: (job: Job) => job.chat!.items.length,
                    render: (job: Job) => (
                        <CommentLink
                            onClick={() => {
                                setSelectedJob(job);
                                setShowChatHistory(true);
                            }}>
                            {job.chat!.items.length} <FontAwesome icon="comment-alt" />
                        </CommentLink>
                    )
                },
                {
                    label: 'Audit History',
                    getValue: (job: Job) => job.dispatches!.items.length,
                    render: (job: Job) => (
                        <CommentLink
                            onClick={() => {
                                setSelectedJob(job);
                                setShowAuditHistory(true);
                            }}>
                            {job.dispatches!.items.length}{' '}
                            <FontAwesome icon="clipboard-list-check" weight="regular" />
                        </CommentLink>
                    )
                }
            ] as Column[];
        },

        setSortColumn(index: number) {
            const column = this.columns[index];

            if (!column || column.sortable === 'false') return;

            if (this.sortColumn === index) this.sortDirection = -this.sortDirection;
            else {
                this.sortColumn = index;
                this.sortDirection = 1;
            }
        },

        get columnCount() {
            return 1 + this.columns.length;
        }
    }));

    function cellRenderer({
        columnIndex,
        key,
        rowIndex,
        style
    }: {
        columnIndex: number;
        key: string;
        rowIndex: number;
        style: CSSProperties;
    }) {
        const isHeader = rowIndex === 0;

        const column = store.columns[columnIndex];

        if (!column) return <span style={style} />;

        if (isHeader) {
            return (
                <GridHeaderCell
                    style={style}
                    key={key}
                    columnIndex={columnIndex}
                    setSortColumn={store.setSortColumn}
                    sortColumn={store.sortColumn}
                    sortDirection={store.sortDirection}>
                    {column.label}
                </GridHeaderCell>
            );
        } else {
            const job = store.sortedJobs[rowIndex - 1];
            const value = column.getValue(job!);
            const rendered = column.render ? column.render(job!) : value;

            const cellStyle = {
                ...style,
                ...(column.align && { textAlign: column.align })
            };

            return (
                <GridCellDisplay style={cellStyle} title={`${value || ''}`}>
                    {rendered}
                </GridCellDisplay>
            );
        }
    }

    function getColumnWidth({ index }: { index: number }) {
        const column = store.columns[index];

        return (column && column.width) || 120;
    }

    return store.sortedJobs.length ? (
        <>
            <ObservableAutoSizer
                customChildren={({ width, height }) => (
                    <ObservableMultiGrid
                        fixedRowCount={1}
                        overscanColumnCount={10}
                        overscanRowCount={40}
                        width={width - 2}
                        height={height - 2}
                        cellRenderer={cellRenderer}
                        rowCount={store.rowCount}
                        columnCount={store.columnCount}
                        rowHeight={32}
                        columnWidth={getColumnWidth}
                    />
                )}
            />

            <PhotoHistoryModal
                isOpen={showPhotoHistory}
                selectedJob={selectedJob}
                onClose={() => setShowPhotoHistory(false)}
            />
            <ChatHistoryModal
                isOpen={showChatHistory}
                selectedJob={selectedJob}
                onClose={() => setShowChatHistory(false)}
            />
            <AuditHistoryModal
                isOpen={showAuditHistory}
                selectedJob={selectedJob}
                onClose={() => setShowAuditHistory(false)}
            />
        </>
    ) : null;
});

const ratingScale = 5;
